import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import {
    NavigationFilterContext,
    NavigationLinkType,
    NavigationMapProvider,
    NavigationNode,
    NavigationNodeConfig,
    NavigationSection,
    SessionService,
    filterNavigationNodes,
    stateMatch
} from '@edl-development/edl-common';
import { Subscription } from 'rxjs';
import { PermissionTypes } from 'src/app/model/enum/permission-types.enum';
import { SchoolYearSchedule } from 'src/app/model/school-year-schedule';
import { PermissionsHelperService } from 'src/app/services/permissions-helper/permissions-helper.service';
import { SessionHelpersService } from 'src/app/services/session-helpers-service/session-helpers.service';
import { PaetepVisibility } from './models/paetep-visibility.model';

@Injectable()
export class PaetepNavigationMapProvider implements NavigationMapProvider {
    private datePipe = new DatePipe('en-US');
    private _viewingCurrentUser = false;
    private _viewingCurrentUserSub?: Subscription;

    constructor(
        private sessionService: SessionService,
        private permissionsHelperService: PermissionsHelperService,
        private sessionHelpersService: SessionHelpersService
    ) { }

    getNavigationMap(context: NavigationFilterContext): NavigationNode[] {
        if (!this._viewingCurrentUserSub) {
            this._viewingCurrentUserSub = this.sessionService.viewingCurrentUser.subscribe(
                (value: boolean) => this._viewingCurrentUser = value
            );
        }

        const config = this.buildNavigationConfig();
        const isVisible = this.createVisibilityPredicate();
        return filterNavigationNodes(config, isVisible, context);
    }

    private createVisibilityPredicate(): (v: PaetepVisibility | undefined) => boolean {
        return (v) => {
            if (!v) return true;

            if (v.requiresBuildings && !this.permissionsHelperService.doIHaveBuildings()) {
                return false;
            }

            if (v.permissionsAll?.length && !this.permissionsHelperService.doIHaveAllPermissions(v.permissionsAll)) {
                return false;
            }

            if (v.permissionsAny?.length && !this.permissionsHelperService.doIHaveAnyPermissions(v.permissionsAny)) {
                return false;
            }

            if (v.condition && !v.condition()) {
                return false;
            }

            return true;
        };
    }

    private buildNavigationConfig(): NavigationNodeConfig<PaetepVisibility>[] {
        return [
            ...this.getSidebarConfig(),
            ...this.getHeaderConfig()
        ];
    }

    private getSidebarConfig(): NavigationNodeConfig<PaetepVisibility>[] {
        const user = this.sessionService.user;

        return [
            {
                id: 'dashboard',
                linkName: 'Dashboard',
                icon: 'fas fa-newspaper',
                link: {
                    type: NavigationLinkType.State,
                    uiSref: 'authenticated.dashboard.view'
                },
                section: [NavigationSection.Sidebar]
            },
            {
                id: 'supervision-management',
                linkName: 'Supervision Management',
                icon: 'fas fa-check-circle',
                link: {
                    type: NavigationLinkType.State,
                    uiSref: 'authenticated.supervision-management.view'
                },
                isActive: (stateService) =>
                    stateService.includes('authenticated.supervision-management')
                    || (stateService.includes('authenticated.user-summary') && !this._viewingCurrentUser),
                visibility: {
                    permissionsAll: [PermissionTypes.PAETEP_SMUI],
                    requiresBuildings: true
                },
                section: [NavigationSection.Sidebar]
            },
            {
                id: 'custom-supervision',
                linkName: 'Custom Supervision',
                icon: 'fas fa-user-group',
                link: {
                    type: NavigationLinkType.State,
                    uiSref: 'authenticated.custom-supervision.view'
                },
                activeStateRoot: 'authenticated.custom-supervision.view',
                visibility: {
                    requiresBuildings: true,
                    condition: () => {
                        const hasModule = this.sessionService.PortalSettings?.Paetep?.Modules?.CustomSupervision ?? false;
                        return hasModule && (user?.IsSuperUser || this.sessionHelpersService.getMyTargetPeerIds().size > 0);
                    }
                },
                section: [NavigationSection.Sidebar]
            },
            {
                id: 'my-observations',
                linkName: 'My Observations',
                icon: 'fas fa-check',
                link: {
                    type: NavigationLinkType.State,
                    uiSref: 'authenticated.user-summary.view',
                    uiParams: { userId: user?.UserID }
                },
                isActive: (stateService) =>
                    stateService.includes('authenticated.user-summary') && this._viewingCurrentUser,
                visibility: {
                    permissionsAny: [PermissionTypes.PAETEP_USER_SUMMARY_CAN_VIEW_MY_OBSERVATIONS, PermissionTypes.CUSTOM_SUPERVISION_CAN_BE_TARGET],
                    requiresBuildings: true
                },
                section: [NavigationSection.Sidebar]
            },
            {
                id: 'attachment-management',
                linkName: 'Attachment Management',
                icon: 'fas fa-paperclip',
                link: {
                    type: NavigationLinkType.State,
                    uiSref: 'authenticated.attachments.myattachments',
                    uiParams: { userId: user?.UserID }
                },
                visibility: {
                    permissionsAll: [PermissionTypes.FILE_ATTACHMENTS]
                },
                section: [NavigationSection.Sidebar]
            },
            {
                id: 'reporting',
                linkName: 'Reporting',
                icon: 'far fa-chart-bar',
                link: {
                    type: NavigationLinkType.State,
                    uiSref: 'authenticated.reporting',
                    uiOptions: { reload: true }
                },
                activeStateRoot: 'authenticated.reporting',
                visibility: {
                    permissionsAny: [PermissionTypes.REPORTING],
                    requiresBuildings: true
                },
                section: [NavigationSection.Sidebar]
            },
            {
                id: 'user-management',
                linkName: 'User Management',
                icon: 'fas fa-user',
                link: { type: NavigationLinkType.None },
                activeStateRoot: 'authenticated.users',
                visibility: {
                    permissionsAll: [PermissionTypes.ADMINISTRATION_USER_MANAGEMENT]
                },
                section: [NavigationSection.Sidebar],
                children: [
                    {
                        id: 'users-all',
                        linkName: 'All Users',
                        link: {
                            type: NavigationLinkType.State,
                            uiSref: 'authenticated.users.view',
                            uiParams: { page: 1 },
                            uiOptions: { reload: true }
                        },
                        isActive: stateMatch(
                            [{ state: 'authenticated.users' }],
                            [{ state: 'authenticated.users.onboarding' }]
                        )
                    },
                    {
                        id: 'users-onboarding',
                        linkName: 'User Onboard & Update',
                        link: {
                            type: NavigationLinkType.State,
                            uiSref: 'authenticated.users.onboarding'
                        }
                    }
                ]
            },
            {
                id: 'administration',
                linkName: 'Administration',
                icon: 'fas fa-cog',
                link: { type: NavigationLinkType.None },
                isActive: stateMatch([
                    { state: 'authenticated.administration' },
                    { state: 'authenticated.buildings' },
                    { state: 'authenticated.custom-form-builder' },
                    { state: 'authenticated.assignments' },
                    { state: 'authenticated.preset-management' },
                    { state: 'authenticated.custom-supervision.management' }
                ]),
                visibility: {
                    permissionsAny: [PermissionTypes.ADMINISTRATION, PermissionTypes.PAETEP_CUSTOM_FORMS_BUILDER],
                    requiresBuildings: true
                },
                section: [NavigationSection.Sidebar],
                children: [
                    {
                        id: 'admin-welcome',
                        linkName: 'Welcome Page',
                        link: {
                            type: NavigationLinkType.State,
                            uiSref: 'authenticated.administration.landing',
                            uiOptions: { reload: true }
                        },
                        visibility: {
                            permissionsAny: [PermissionTypes.ADMINISTRATION]
                        }
                    },
                    {
                        id: 'admin-assignments',
                        linkName: 'Assignment Management',
                        link: {
                            type: NavigationLinkType.State,
                            uiSref: 'authenticated.assignments.view',
                            uiOptions: { reload: true }
                        },
                        activeStateRoot: 'authenticated.assignments',
                        visibility: {
                            permissionsAll: [PermissionTypes.ASSIGNMENTS_CAN_ASSIGN_TO_BUILDINGS]
                        }
                    },
                    {
                        id: 'admin-buildings',
                        linkName: 'Building Management',
                        link: {
                            type: NavigationLinkType.State,
                            uiSref: 'authenticated.buildings.view',
                            uiOptions: { reload: true }
                        },
                        activeStateRoot: 'authenticated.buildings',
                        visibility: {
                            permissionsAll: [PermissionTypes.ADMINISTRATION_BUILDINGS]
                        }
                    },
                    {
                        id: 'admin-custom-supervision',
                        linkName: 'Custom Supervision Management',
                        link: {
                            type: NavigationLinkType.State,
                            uiSref: 'authenticated.custom-supervision.management',
                            uiOptions: { reload: true }
                        },
                        activeStateRoot: 'authenticated.custom-supervision.management',
                        visibility: {
                            permissionsAll: [PermissionTypes.CUSTOM_SUPERVISION_CAN_MANAGE_ASSIGNMENTS]
                        }
                    },
                    {
                        id: 'admin-custom-forms',
                        linkName: 'Custom Form Management',
                        link: {
                            type: NavigationLinkType.State,
                            uiSref: 'authenticated.custom-form-builder.custom-forms',
                            uiOptions: { reload: true }
                        },
                        activeStateRoot: 'authenticated.custom-form-builder',
                        visibility: {
                            permissionsAll: [PermissionTypes.PAETEP_CUSTOM_FORMS_BUILDER]
                        }
                    },
                    {
                        id: 'admin-rating-forms',
                        linkName: 'Rating Form Management',
                        link: {
                            type: NavigationLinkType.State,
                            uiSref: 'authenticated.administration.ratingform',
                            uiOptions: { reload: true }
                        },
                        visibility: {
                            permissionsAll: [PermissionTypes.ADMINISTRATION_RATING_FORM_MANAGEMENT]
                        }
                    },
                    {
                        id: 'admin-portal-settings',
                        linkName: 'Portal Settings',
                        link: {
                            type: NavigationLinkType.State,
                            uiSref: 'authenticated.administration.portalSettings',
                            uiOptions: { reload: true }
                        },
                        visibility: {
                            permissionsAll: [PermissionTypes.IS_SUPER_ADMIN]
                        }
                    },
                    {
                        id: 'admin-presets',
                        linkName: 'Preset Management',
                        link: {
                            type: NavigationLinkType.State,
                            uiSref: 'authenticated.preset-management.pg.view',
                            uiOptions: { reload: true }
                        },
                        activeStateRoot: 'authenticated.preset-management',
                        visibility: {
                            permissionsAll: [PermissionTypes.PRESET_MANAGEMENT]
                        }
                    },
                    {
                        id: 'admin-pvaas',
                        linkName: 'Teacher Specific Data Upload',
                        link: {
                            type: NavigationLinkType.State,
                            uiSref: 'authenticated.administration.pvaas-upload',
                            uiOptions: { reload: true }
                        },
                        visibility: {
                            permissionsAll: [PermissionTypes.PVAAS_UPLOAD_MANAGEMENT]
                        }
                    },
                    {
                        id: 'admin-styling',
                        linkName: 'Styling Example',
                        link: {
                            type: NavigationLinkType.State,
                            uiSref: 'authenticated.administration.styleExample',
                            uiParams: { page: 1 },
                            uiOptions: { reload: true }
                        },
                        visibility: {
                            permissionsAll: [PermissionTypes.IS_SUPER_ADMIN]
                        }
                    },
                    {
                        id: 'admin-alert',
                        linkName: 'Alert Content',
                        link: {
                            type: NavigationLinkType.State,
                            uiSref: 'authenticated.administration.alert',
                            uiParams: { page: 1 },
                            uiOptions: { reload: true }
                        },
                        visibility: {
                            permissionsAll: [PermissionTypes.IS_SUPER_ADMIN]
                        }
                    }
                ]
            },
            {
                id: 'permissions',
                linkName: 'Permissions',
                icon: 'fas fa-lock',
                link: { type: NavigationLinkType.None },
                activeStateRoot: 'authenticated.permissions',
                visibility: {
                    permissionsAll: [PermissionTypes.IS_SUPER_ADMIN, PermissionTypes.ADMINISTRATION_PERMISSIONS]
                },
                section: [NavigationSection.Sidebar],
                children: [
                    {
                        id: 'permissions-settings',
                        linkName: 'Permissions Settings',
                        link: {
                            type: NavigationLinkType.State,
                            uiSref: 'authenticated.permissions.permissionSettings',
                            uiOptions: { reload: true }
                        },
                        visibility: {
                            permissionsAll: [PermissionTypes.ADMINISTRATION_PERMISSIONS_CAN_VIEW_ALL]
                        }
                    },
                    {
                        id: 'permissions-edit',
                        linkName: 'Edit Permissions',
                        link: {
                            type: NavigationLinkType.State,
                            uiSref: 'authenticated.permissions.edit',
                            uiOptions: { reload: true }
                        },
                        visibility: {
                            permissionsAll: [PermissionTypes.ADMINISTRATION_PERMISSIONS_CAN_EDIT_ROLES]
                        }
                    }
                ]
            }
        ];
    }

    private getHeaderConfig(): NavigationNodeConfig<PaetepVisibility>[] {
        const user = this.sessionService.user;
        const messagesParams = this.generateMessagesParams();

        return [
            {
                id: 'notifications',
                linkName: 'Notifications',
                icon: 'fas fa-bell',
                link: {
                    type: NavigationLinkType.State,
                    uiSref: 'authenticated.messages.my-messages',
                    uiParams: messagesParams
                },
                section: [NavigationSection.Header]
            },
            {
                id: 'support',
                linkName: 'Support',
                icon: 'fas fa-question-circle',
                link: {
                    type: NavigationLinkType.External,
                    href: 'https://edulinksolutions.freshdesk.com/support/solutions',
                    target: '_blank'
                },
                section: [NavigationSection.Header]
            },
            {
                id: 'update-profile',
                linkName: 'Update Profile',
                icon: 'fas fa-user-edit',
                link: {
                    type: NavigationLinkType.State,
                    uiSref: 'authenticated.users.user.edit',
                    uiParams: { userId: user?.UserID }
                },
                section: [NavigationSection.Header]
            }
        ];
    }

    private generateMessagesParams(): { page: number, start: string | null, end: string | null } {
        const schoolYear = new SchoolYearSchedule();
        return {
            page: 1,
            start: this.datePipe.transform(schoolYear.StartDate, 'MM/dd/yyyy'),
            end: this.datePipe.transform(schoolYear.EndDate, 'MM/dd/yyyy')
        };
    }
}
